// All AI-triggering callbacks for the NewSession page: posture analysis, SOAP
// drafting, protocol suggestion (+ save/apply), wrap-up generation, and photo
// comparison. Plus the shared `buildContinuity()` payload helper.
//
// Extracted from NewSession.jsx during Iter AN Phase 2. Toasts, payload
// shapes, side-effects, and error handling preserved exactly.

import { useCallback, useState } from "react";
import { toast } from "sonner";
import { api } from "../../../lib/api";


export function useSessionAI(state, setters) {
  const {
    client, previousSession, progressSinceLast, clinicalCapture,
    photos, intakeNotes, chiefComplaintObj, workedAreas, analysis,
    wrapUp, outcome, clientId, phase, suggestedProtocol,
  } = state;
  const {
    setAnalysis, setWorkedAreas, setOutcome, setPhase, saveDraft,
    setSoap, setSuggestedProtocol, setSelectedProtocolId, setProtocols,
    setPhotoComparisonSummary, setWrapUp,
  } = setters;

  const [draftingSoap, setDraftingSoap] = useState(false);
  const [suggestingProtocol, setSuggestingProtocol] = useState(false);
  const [suggestingWrapUp, setSuggestingWrapUp] = useState(false);
  const [generatingComparison, setGeneratingComparison] = useState(false);

  const buildContinuity = useCallback(() => {
    const snap = client ? {
      medical_history: client.medical_history || [],
      clinical_patterns: client.clinical_patterns || [],
      long_term_goals: client.long_term_goals || [],
      clinical_findings: client.clinical_findings || [],
    } : null;
    return {
      clinical_snapshot: snap,
      previous_session_summary: previousSession || null,
      progress_since_last: progressSinceLast || null,
      current_session_captures: clinicalCapture,
    };
  }, [client, previousSession, progressSinceLast, clinicalCapture]);

  const runAnalysis = useCallback(async () => {
    const prev = phase;
    setPhase("pre-photos-analyzing");
    try {
      const r = await api.post("/posture/analyze", {
        front_path: photos.front,
        side_path: photos.side,
        back_path: photos.back,
        intake_notes: intakeNotes || chiefComplaintObj.chief_complaint || null,
        ...buildContinuity(),
      });
      setAnalysis(r.data);
      setWorkedAreas(r.data.focus_muscles || []);
      setOutcome((o) => ({ ...o, techniques_used: (r.data.recommended_techniques || []).map((t) => t.technique) }));
      setPhase("pre-photos");
      setTimeout(() => saveDraft({ ai_analysis: r.data, worked_areas: r.data.focus_muscles || [] }), 0);
    } catch (e) {
      toast.error("AI analysis failed. " + (e?.response?.data?.detail || ""));
      setPhase(prev);
    }
  }, [phase, photos, intakeNotes, chiefComplaintObj, buildContinuity, setAnalysis, setWorkedAreas, setOutcome, setPhase, saveDraft]);

  const draftSoap = useCallback(async () => {
    if (clinicalCapture.length === 0) {
      const ok = window.confirm("No live captures yet — your SOAP will be less specific. Continue anyway?");
      if (!ok) return;
    }
    setDraftingSoap(true);
    try {
      const r = await api.post("/soap/suggest", {
        analysis, worked_areas: workedAreas, outcome,
        client_name: client?.name, client_age: client?.age,
        intake_notes: intakeNotes || chiefComplaintObj.chief_complaint || null,
        wrap_up: wrapUp || null,
        ...buildContinuity(),
      });
      setSoap(r.data);
      toast.success("AI draft inserted — feel free to edit");
    } catch (e) {
      toast.error("Could not draft SOAP. " + (e?.response?.data?.detail || ""));
    } finally {
      setDraftingSoap(false);
    }
  }, [clinicalCapture, analysis, workedAreas, outcome, client, intakeNotes, chiefComplaintObj, wrapUp, buildContinuity, setSoap]);

  const suggestProtocol = useCallback(async () => {
    setSuggestingProtocol(true);
    try {
      const r = await api.post("/protocols/suggest", {
        analysis, worked_areas: workedAreas, client_name: client?.name,
        intake_notes: intakeNotes || chiefComplaintObj.chief_complaint || null,
      });
      setSuggestedProtocol(r.data);
      toast.success("Protocol suggested");
    } catch (e) {
      toast.error("Could not suggest. " + (e?.response?.data?.detail || ""));
    } finally {
      setSuggestingProtocol(false);
    }
  }, [analysis, workedAreas, client, intakeNotes, chiefComplaintObj, setSuggestedProtocol]);

  const saveSuggestedProtocol = useCallback(async () => {
    if (!suggestedProtocol) return;
    try {
      const r = await api.post("/protocols", suggestedProtocol);
      setProtocols((p) => [r.data, ...p]);
      setSelectedProtocolId(r.data.id);
      toast.success("Protocol saved to library");
    } catch {
      toast.error("Could not save protocol");
    }
  }, [suggestedProtocol, setProtocols, setSelectedProtocolId]);

  const applyProtocol = useCallback((proto) => {
    if (!proto) return;
    setWorkedAreas((prev) => [...new Set([...(prev || []), ...(proto.focus_muscles || [])])]);
    setOutcome((o) => ({ ...o, techniques_used: [...new Set([...(o.techniques_used || []), ...(proto.steps || []).map((s) => s.technique)])] }));
    toast.success(`Applied "${proto.name}"`);
  }, [setWorkedAreas, setOutcome]);

  const runSuggestWrapUp = useCallback(async () => {
    if (!(wrapUp.therapist_summary || "").trim()) {
      toast.error("Tap the mic and describe what you did first.");
      return;
    }
    setSuggestingWrapUp(true);
    try {
      const r = await api.post("/wrap-up/suggest", {
        therapist_summary: wrapUp.therapist_summary,
        intake_notes: intakeNotes || chiefComplaintObj.chief_complaint || null,
        analysis,
        worked_areas: workedAreas,
        client_name: client?.name,
        client_age: client?.age,
        ...buildContinuity(),
      });
      const next = {
        ...wrapUp,
        home_care: r.data.home_care || [],
        best_return: r.data.best_return || { when: "", why: "" },
        good_return: r.data.good_return || { when: "", why: "" },
      };
      setWrapUp(next);
      toast.success("Home care & return plan ready");
      setTimeout(() => saveDraft({ wrap_up: next }), 0);
    } catch (e) {
      toast.error("Could not generate wrap-up. " + (e?.response?.data?.detail || ""));
    } finally {
      setSuggestingWrapUp(false);
    }
  }, [wrapUp, intakeNotes, chiefComplaintObj, analysis, workedAreas, client, buildContinuity, saveDraft, setWrapUp]);

  const runPhotoComparison = useCallback(async (comparedFromSessionId) => {
    if (!comparedFromSessionId) {
      toast.error("Pick a previous photo session first.");
      return;
    }
    setGeneratingComparison(true);
    try {
      const r = await api.post("/photo-comparison/suggest", {
        client_id: clientId,
        previous_session_id: comparedFromSessionId,
        intake_notes: intakeNotes || chiefComplaintObj.chief_complaint || null,
        progress_since_last: progressSinceLast,
        clinical_snapshot: buildContinuity().clinical_snapshot,
      });
      setPhotoComparisonSummary(r.data.summary || "");
      toast.success("Comparison summary ready");
      setTimeout(() => saveDraft({ photo_comparison_summary: r.data.summary }), 0);
    } catch (e) {
      toast.error("Could not generate comparison. " + (e?.response?.data?.detail || ""));
    } finally {
      setGeneratingComparison(false);
    }
  }, [clientId, intakeNotes, chiefComplaintObj, progressSinceLast, buildContinuity, saveDraft, setPhotoComparisonSummary]);

  return {
    buildContinuity,
    runAnalysis,
    draftSoap, draftingSoap,
    suggestProtocol, suggestingProtocol, saveSuggestedProtocol, applyProtocol,
    runSuggestWrapUp, suggestingWrapUp,
    runPhotoComparison, generatingComparison,
  };
}
